import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { format } from 'date-fns';
import axios from 'axios';
import { UserCog, Plus, Pencil, Trash2, Loader2, Mail, Clock, XCircle, Upload, Download, AlertTriangle, CheckCircle2, RotateCcw, Search, ListFilter, ArrowUp, ArrowDown, ChevronsUpDown, X } from 'lucide-react';
import { UserRole, useAuth } from '../contexts/AuthContext';
import { clientSupplierService } from '../services/clientSupplierService';
import { isSelfApprovingRole } from '@timesheet/schemas';
import {
  userService,
  type ApproverCandidate,
  type UserImportPreviewResult,
  type UserImportPreviewRow,
} from '../services/userService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { PageTransition } from '../components/PageTransition';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { getRoleLabel } from '../lib/role-labels';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

interface User {
  id: number;
  name: string;
  email: string;
  codiceFiscale?: string | null;
  role: UserRole;
  relationshipType?: UserRelationshipType;
  isActive: boolean;
  supplierId?: number | null;
  supplier?: { id: number; name: string; type?: ClientType } | null;
  approverId?: number | null;
  approver?: ApproverCandidate | null;
  tenant?: { id: number; name: string } | null;
}

type ClientType = 'CLIENT' | 'SUPPLIER' | 'BOTH';

type UserRelationshipType = 'INTERNAL_EMPLOYEE' | 'FREELANCER' | 'SUBCONTRACTOR' | 'OTHER';

interface UserFormData {
  name: string;
  email: string;
  codiceFiscale: string;
  role: UserRole;
  relationshipType: UserRelationshipType;
  supplierId?: number | null;
  approverId: number | null;
}

interface UserDeletionStatus {
  canPermanentlyDelete: boolean;
  reason?: 'USER_IS_ACTIVE' | 'HAS_REFERENCES';
}

interface UserReactivationTarget {
  mode: 'user' | 'email';
  email: string;
  user?: User;
}

interface SupplierOption {
  id: number;
  name: string;
  type: ClientType;
}

type StatusFilter = 'active' | 'inactive' | 'all';
type SortKey = 'name' | 'role' | 'relationshipType' | 'supplier' | 'approver' | 'status';
type SortDirection = 'asc' | 'desc';

/** Link filter values: 'all' | 'internal' | a ClientType | `id:<clientSupplierId>`. */
type LinkFilter = string;

interface PendingInvitation {
  id: number;
  email: string;
  name: string | null;
  role: string;
  relationshipType?: UserRelationshipType;
  expiresAt: string;
  createdAt: string;
  isExpired: boolean;
}

const relationshipTypeOptions: UserRelationshipType[] = [
  'INTERNAL_EMPLOYEE',
  'FREELANCER',
  'SUBCONTRACTOR',
  'OTHER',
];

const getRelationshipTypeLabel = (
  relationshipType: UserRelationshipType | string | undefined,
  t: any,
): string => {
  if (!relationshipType) {
    return t('pages.usersManager.relationshipTypes.INTERNAL_EMPLOYEE', 'Internal employee');
  }

  return t(`pages.usersManager.relationshipTypes.${relationshipType}`, relationshipType);
};

const getRoleBadgeVariant = (role: UserRole): 'default' | 'secondary' | 'outline' => {
  switch (role) {
    case 'ADMIN':
    case 'SUPER_ADMIN':
      return 'default';
    case 'MANAGER':
      return 'secondary';
    default:
      return 'outline';
  }
};

/** Roles sort by privilege, not alphabetically — the ranking is the information. */
/** Label shown in the list: self for Admin/Super Admin, the approver name, or nothing. */
const getApproverLabel = (user: User, t: TFunction): string | null => {
  if (isSelfApprovingRole(user.role)) return t('pages.usersManager.selfApprover', 'Self');
  return user.approver?.name ?? null;
};

const roleRank: Record<string, number> = {
  [UserRole.OPERATOR]: 0,
  [UserRole.MANAGER]: 1,
  [UserRole.ACCOUNTANT]: 2,
  [UserRole.ADMIN]: 3,
  [UserRole.SUPER_ADMIN]: 4,
};

const relationshipRank: Record<string, number> = {
  INTERNAL_EMPLOYEE: 0,
  FREELANCER: 1,
  SUBCONTRACTOR: 2,
  OTHER: 3,
};

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) || '' : '';
  return (first + last).toUpperCase() || '?';
};

const getClientTypeBadgeVariant = (type?: ClientType): 'client' | 'supplier' | 'secondary' => {
  switch (type) {
    case 'CLIENT':
      return 'client';
    case 'SUPPLIER':
      return 'supplier';
    default:
      return 'secondary';
  }
};

const getClientTypeLabel = (type: ClientType | undefined, t: any): string => {
  switch (type) {
    case 'CLIENT':
      return t('pages.usersManager.clientTypes.CLIENT', 'Client');
    case 'SUPPLIER':
      return t('pages.usersManager.clientTypes.SUPPLIER', 'Supplier');
    case 'BOTH':
      return t('pages.usersManager.clientTypes.BOTH', 'Client and supplier');
    default:
      return '';
  }
};

const AdminUsersManager: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  // ─── Filter + sort state ─────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('all');
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [inviteSent, setInviteSent] = useState(false);
  /** Shown inside the user dialog: page-level alerts sit behind the overlay and go unnoticed. */
  const [formError, setFormError] = useState<string>('');
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    codiceFiscale: '',
    role: UserRole.OPERATOR,
    relationshipType: 'INTERNAL_EMPLOYEE',
    approverId: null,
  });
  const [approverCandidates, setApproverCandidates] = useState<ApproverCandidate[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteStatusLoading, setDeleteStatusLoading] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<UserDeletionStatus | null>(null);
  const [deleteDialogError, setDeleteDialogError] = useState<string | null>(null);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<UserReactivationTarget | null>(null);
  const [reactivatingUser, setReactivatingUser] = useState(false);

  // ─── Import state ────────────────────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<UserImportPreviewResult | null>(null);
  const [importTenantMappings, setImportTenantMappings] = useState<Record<string, number>>({});
  const [importSupplierMappings, setImportSupplierMappings] = useState<Record<string, number>>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogError, setImportDialogError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [invalidPreviewRow, setInvalidPreviewRow] = useState<UserImportPreviewRow | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const isImportBusy = previewLoading || importing;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isSuperAdmin
        ? `${API_URL}/users/super-admin/all-users`
        : `${API_URL}/users`;
      // Always load both active and inactive users: the Status filter narrows the
      // list client-side, so switching it never costs a round trip.
      const response = await axios.get(`${endpoint}?includeInactive=true`);
      setUsers(response.data as User[]);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(t('pages.usersManager.errors.fetch', 'Failed to fetch users'));
    } finally {
      setLoading(false);
    }
  }, [t, isSuperAdmin]);

  const fetchTenants = useCallback(async () => {
    try {
      if (isSuperAdmin) {
        const response = await axios.get(`${API_URL}/tenants`);
        setTenants(response.data);
        return;
      }

      if (currentUser?.tenantId) {
        const response = await axios.get(
          `${API_URL}/tenants/${currentUser.tenantId}`
        );
        setTenants([response.data]);
        return;
      }

      setTenants([]);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenants([]);
    }
  }, [isSuperAdmin, currentUser?.tenantId]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const all = await clientSupplierService.getAll();
      setSuppliers(
        all
          .filter((cs) => cs.type === 'SUPPLIER' || cs.type === 'BOTH')
          .map((cs) => ({ id: cs.id, name: cs.name, type: cs.type as ClientType })),
      );
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, []);

  /**
   * Eligible approvers come from the backend so the rule (active Super Admin / Admin /
   * Manager of the same tenant) lives in one place. Super Admins see other tenants' users,
   * so the candidate list follows the tenant of the user being edited.
   */
  const loadApproverCandidates = useCallback(async (tenantId?: number) => {
    try {
      setLoadingApprovers(true);
      setApproverCandidates(await userService.getApproverCandidates(tenantId));
    } catch (error) {
      console.error('Error fetching approver candidates:', error);
      setApproverCandidates([]);
      setFormError(t('pages.usersManager.errors.fetchApprovers', 'Failed to load available approvers'));
    } finally {
      setLoadingApprovers(false);
    }
  }, [t]);

  const fetchPendingInvites = useCallback(async () => {
    try {
      setLoadingInvites(true);
      const response = await axios.get(`${API_URL}/invitations/pending`);
      setPendingInvites(response.data);
    } catch (error) {
      console.error('Error fetching pending invites:', error);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const handleRevokeInvite = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/invitations/${id}`);
      await fetchPendingInvites();
    } catch (error: any) {
      console.error('Error revoking invite:', error);
      setError(
        error.response?.data?.message ||
          t('pages.usersManager.errors.revokeInvite', 'Failed to revoke invitation')
      );
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTenants();
    fetchSuppliers();
    fetchPendingInvites();
  }, [fetchUsers, fetchTenants, fetchSuppliers, fetchPendingInvites]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchPendingInvites();
    }, 30000);

    const handleFocus = () => {
      fetchPendingInvites();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchPendingInvites]);

  // ─── Import handlers ─────────────────────────────────────────────────────────
  const waitForUiPaint = (): Promise<void> => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

  const resetImportDialog = useCallback(() => {
    setSelectedImportFile(null);
    setImportPreview(null);
    setImportTenantMappings({});
    setImportSupplierMappings({});
    setPreviewLoading(false);
    setImporting(false);
    setImportDialogError(null);
    setIsDraggingFile(false);
    setInvalidPreviewRow(null);
    if (importFileInputRef.current) importFileInputRef.current.value = '';
  }, []);

  const handleOpenImport = () => {
    setImportOpen(true);
    resetImportDialog();
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    resetImportDialog();
  };

  const previewImportFile = useCallback(async (
    file: File,
    tenantMappings: Record<string, number>,
    supplierMappings: Record<string, number>,
  ) => {
    setSelectedImportFile(file);
    setPreviewLoading(true);
    setImportDialogError(null);
    setImportPreview(null);
    await waitForUiPaint();
    try {
      const preview = await userService.previewImport(file, tenantMappings, supplierMappings);
      setImportPreview(preview);
    } catch {
      setImportDialogError(t('pages.usersManager.errors.import', 'Failed to import CSV file'));
    } finally {
      setPreviewLoading(false);
    }
  }, [t]);

  const handleImportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isImportBusy) return;
    const file = event.target.files?.[0];
    if (!file) return;
    await previewImportFile(file, importTenantMappings, importSupplierMappings);
  };

  const handleImportDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (isImportBusy) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await previewImportFile(file, importTenantMappings, importSupplierMappings);
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const blob = await userService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users-import-template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setImportDialogError(t('pages.usersManager.errors.import', 'Failed to import CSV file'));
    }
  };

  const handleRefreshPreviewWithMappings = async () => {
    if (!selectedImportFile) return;
    await previewImportFile(selectedImportFile, importTenantMappings, importSupplierMappings);
  };

  const handleImportConfirm = async () => {
    if (!selectedImportFile) return;
    try {
      setImporting(true);
      await waitForUiPaint();
      const result = await userService.import(selectedImportFile, importTenantMappings, importSupplierMappings);
      if (result.created > 0) await fetchUsers();
      handleCloseImport();
    } catch {
      setImportDialogError(t('pages.usersManager.errors.import', 'Failed to import CSV file'));
    } finally {
      setImporting(false);
    }
  };

  const getInvalidRowMessage = (row: UserImportPreviewRow): string => {
    if (!row.error) return t('pages.usersManager.importErrors.generic', 'Some fields are invalid.');
    const mapErr = (e: string): string => {
      if (/^duplicateEmail:/i.test(e)) return t('pages.usersManager.importErrors.duplicateEmail', 'Email already exists or is duplicated in the CSV.');
      if (/^email:/i.test(e)) return t('pages.usersManager.importErrors.invalidEmail', 'Invalid email address.');
      if (/^name:/i.test(e)) return t('pages.usersManager.importErrors.nameRequired', 'Name is required.');
      if (/^role:/i.test(e)) return t('pages.usersManager.importErrors.roleInvalid', 'Invalid role value. Valid roles: OPERATOR, MANAGER, ACCOUNTANT, ADMIN.');
      if (/^tenant:/i.test(e)) return t('pages.usersManager.importErrors.tenantNotFound', 'Company not found. Select an existing company for this row.');
      if (/^supplier:/i.test(e)) return t('pages.usersManager.importErrors.supplierNotFound', 'Supplier not found. Select an existing supplier for this row.');
      return t('pages.usersManager.importErrors.generic', 'Some fields are invalid.');
    };
    return row.error.split(';').map((p) => p.trim()).filter(Boolean).map(mapErr).join(' ');
  };

  const rowHasFieldError = (row: UserImportPreviewRow, prefix: string): boolean =>
    !row.valid && !!row.error && row.error.split(';').some((p) => new RegExp(`^${prefix}:`, 'i').test(p.trim()));

  const rowNeedsSupplierMapping = (row: UserImportPreviewRow): boolean => {
    if (!row.supplierName) return false;
    return importPreview?.unresolvedSuppliers.includes(row.supplierName) ?? false;
  };

  const rowNeedsTenantMapping = (row: UserImportPreviewRow): boolean => {
    if (!row.tenantName) return false;
    return importPreview?.unresolvedTenants.includes(row.tenantName) ?? false;
  };

  const handleOpen = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        codiceFiscale: user.codiceFiscale ?? '',
        role: user.role,
        relationshipType: user.relationshipType || 'INTERNAL_EMPLOYEE',
        supplierId: user.supplierId ?? null,
        approverId: user.approverId ?? user.approver?.id ?? null,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        codiceFiscale: '',
        role: UserRole.OPERATOR,
        relationshipType: 'INTERNAL_EMPLOYEE',
        supplierId: suppliers[0]?.id ?? null,
        approverId: null,
      });
    }
    setInviteSent(false);
    setFormError('');
    setOpen(true);
    void loadApproverCandidates(isSuperAdmin ? user?.tenant?.id : undefined);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
    setInviteSent(false);
    setFormError('');
    setError('');
  };

  const openReactivateDialogForEmail = (email: string) => {
    setReactivateTarget({ mode: 'email', email });
    setReactivateDialogOpen(true);
  };

  const openReactivateDialogForUser = (user: User) => {
    setReactivateTarget({ mode: 'user', email: user.email, user });
    setReactivateDialogOpen(true);
  };

  const handleConfirmReactivate = async () => {
    if (!reactivateTarget) return;

    try {
      setReactivatingUser(true);

      if (reactivateTarget.mode === 'user' && reactivateTarget.user) {
        await axios.patch(`${API_URL}/users/${reactivateTarget.user.id}/reactivate`);
      } else {
        await axios.patch(`${API_URL}/users/reactivate-by-email`, {
          email: reactivateTarget.email,
        });
        await fetchPendingInvites();
        handleClose();
      }

      await fetchUsers();
      setReactivateDialogOpen(false);
      setReactivateTarget(null);
    } catch (error: any) {
      console.error('Error reactivating user:', error);
      setError(error.response?.data?.message || t('pages.usersManager.errors.reactivate', 'Failed to reactivate user'));
    } finally {
      setReactivatingUser(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.supplierId) {
      setFormError(t('pages.usersManager.errors.supplierRequired', 'Client/Supplier link is required for all users'));
      return;
    }
    if (needsApprover && !formData.approverId) {
      setFormError(t('pages.usersManager.errors.approverRequired', "Select the user's direct approver"));
      return;
    }
    const approverId = needsApprover ? formData.approverId : null;

    try {
      setSaving(true);
      setFormError('');
      if (editingUser) {
        await axios.patch(`${API_URL}/users/${editingUser.id}`, {
          name: formData.name,
          email: formData.email,
          codiceFiscale: formData.codiceFiscale.trim() || null,
          role: formData.role,
          relationshipType: formData.relationshipType,
          supplierId: formData.supplierId || null,
          approverId,
        });
        fetchUsers();
        handleClose();
      } else {
        await axios.post(`${API_URL}/users/invite`, {
          name: formData.name,
          email: formData.email,
          codiceFiscale: formData.codiceFiscale.trim() || null,
          role: formData.role,
          relationshipType: formData.relationshipType,
          supplierId: formData.supplierId || null,
          approverId,
        });
        setInviteSent(true);
        await fetchPendingInvites();
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      const apiMessage = error.response?.data?.message;
      if (
        !editingUser
        && typeof apiMessage === 'string'
        && apiMessage.toLowerCase().includes('already exists')
      ) {
        openReactivateDialogForEmail(formData.email);
        return;
      }

      setFormError(apiMessage || t('pages.usersManager.errors.save', 'Failed to save user'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteDialogError(null);
    setDeleteStatus(null);
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    const loadDeletionStatus = async () => {
      if (!deleteDialogOpen || !userToDelete || userToDelete.isActive) {
        setDeleteStatus(null);
        setDeleteStatusLoading(false);
        return;
      }

      try {
        setDeleteStatusLoading(true);
        const response = await axios.get(
          `${API_URL}/users/${userToDelete.id}/deletion-status`
        );
        setDeleteStatus(response.data);
      } catch (error: any) {
        console.error('Error fetching deletion status:', error);
        setDeleteDialogError(
          error.response?.data?.message
          || t('pages.usersManager.errors.fetchDeleteStatus', 'Failed to verify permanent deletion availability')
        );
      } finally {
        setDeleteStatusLoading(false);
      }
    };

    loadDeletionStatus();
  }, [deleteDialogOpen, userToDelete, t]);

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    if (!userToDelete.isActive && deleteStatus && !deleteStatus.canPermanentlyDelete) {
      return;
    }

    try {
      setDeletingUser(true);
      const suffix = userToDelete.isActive ? '' : '?permanent=true';
      await axios.delete(`${API_URL}/users/${userToDelete.id}${suffix}`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.message || t('pages.usersManager.errors.delete', 'Failed to delete user'));
    } finally {
      setDeletingUser(false);
    }
  };

  const showSupplierField = true;
  const availableRoles = Object.values(UserRole).filter((role) => role !== UserRole.SUPER_ADMIN);
  // Admin and Super Admin approve themselves; everybody else needs a direct approver.
  const needsApprover = !isSelfApprovingRole(formData.role);
  const approverOptions = approverCandidates.filter((candidate) => candidate.id !== editingUser?.id);

  // ─── Filtering, sorting ──────────────────────────────────────────────────────

  /**
   * Client/supplier options are derived from the users actually loaded, so every
   * option in the filter is guaranteed to return at least one row.
   */
  const linkedClientSuppliers = React.useMemo(() => {
    const byId = new Map<number, { id: number; name: string; type?: ClientType }>();
    users.forEach((user) => {
      if (user.supplier) byId.set(user.supplier.id, user.supplier);
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const linkedTypes = React.useMemo(() => {
    const types = new Set<ClientType>();
    linkedClientSuppliers.forEach((cs) => {
      if (cs.type) types.add(cs.type);
    });
    return (['CLIENT', 'SUPPLIER', 'BOTH'] as ClientType[]).filter((type) => types.has(type));
  }, [linkedClientSuppliers]);

  const filteredUsers = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return users.filter((user) => {
      if (statusFilter === 'active' && !user.isActive) return false;
      if (statusFilter === 'inactive' && user.isActive) return false;

      if (roleFilter !== 'all' && user.role !== roleFilter) return false;

      if (relationshipFilter !== 'all') {
        const relationship = user.relationshipType || 'INTERNAL_EMPLOYEE';
        if (relationship !== relationshipFilter) return false;
      }

      if (linkFilter !== 'all') {
        if (linkFilter === 'internal') {
          if (user.supplier) return false;
        } else if (linkFilter.startsWith('id:')) {
          if (String(user.supplier?.id) !== linkFilter.slice(3)) return false;
        } else if (user.supplier?.type !== linkFilter) {
          return false;
        }
      }

      if (query) {
        const haystack = [user.name, user.email, user.codiceFiscale, user.supplier?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [users, searchQuery, statusFilter, roleFilter, relationshipFilter, linkFilter]);

  const sortedUsers = React.useMemo(() => {
    const compare = (a: User, b: User): number => {
      switch (sortKey) {
        case 'role':
          return (roleRank[a.role] ?? 0) - (roleRank[b.role] ?? 0);
        case 'relationshipType':
          return (
            (relationshipRank[a.relationshipType || 'INTERNAL_EMPLOYEE'] ?? 0) -
            (relationshipRank[b.relationshipType || 'INTERNAL_EMPLOYEE'] ?? 0)
          );
        case 'supplier':
          return (a.supplier?.name || '').localeCompare(b.supplier?.name || '');
        case 'approver':
          return (getApproverLabel(a, t) || '').localeCompare(getApproverLabel(b, t) || '');
        case 'status':
          return Number(b.isActive) - Number(a.isActive);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    };

    return [...filteredUsers].sort((a, b) => {
      const result = compare(a, b);
      // Fall back to name so equal keys keep a stable, predictable order.
      const tiebreak = result !== 0 ? result : a.name.localeCompare(b.name);
      return sortDirection === 'asc' ? tiebreak : -tiebreak;
    });
  }, [filteredUsers, sortKey, sortDirection, t]);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    statusFilter !== 'active' ||
    roleFilter !== 'all' ||
    relationshipFilter !== 'all' ||
    linkFilter !== 'all';

  const activeFilterCount = [
    searchQuery.trim() !== '',
    statusFilter !== 'active',
    roleFilter !== 'all',
    relationshipFilter !== 'all',
    linkFilter !== 'all',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('active');
    setRoleFilter('all');
    setRelationshipFilter('all');
    setLinkFilter('all');
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const renderSortableHead = (key: SortKey, label: string, className?: string) => {
    const isActive = sortKey === key;
    const SortIcon = !isActive ? ChevronsUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;

    return (
      <TableHead
        className={className}
        aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => toggleSort(key)}
          className="group -mx-2 inline-flex items-center gap-1.5 rounded px-2 py-1 font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
          <SortIcon
            className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60'
            }`}
            aria-hidden="true"
          />
        </button>
      </TableHead>
    );
  };

  // ─── Rendering ───────────────────────────────────────────────────────────────

  /** Self for Admin/Super Admin, the approver name, or an amber "none" so gaps stand out. */
  const renderApproverCell = (user: User) => {
    if (isSelfApprovingRole(user.role)) {
      return <span className="text-muted-foreground">{t('pages.usersManager.selfApprover', 'Self')}</span>;
    }
    if (user.approver) {
      return <span className="truncate">{user.approver.name}</span>;
    }
    return (
      <span className="text-amber-600 dark:text-amber-400">
        {t('pages.usersManager.noApprover', 'No approver')}
      </span>
    );
  };

  const renderClientSupplierCell = (user: User) => {
    if (!user.supplier) {
      return (
        <span className="text-muted-foreground">
          {t('pages.usersManager.internal', 'Internal')}
        </span>
      );
    }

    return (
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate">{user.supplier.name}</span>
        {user.supplier.type && (
          <Badge
            variant={getClientTypeBadgeVariant(user.supplier.type)}
            className="w-fit text-[10px] font-medium"
          >
            {getClientTypeLabel(user.supplier.type, t)}
          </Badge>
        )}
      </div>
    );
  };

  const renderFilters = () => (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ListFilter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {t('pages.usersManager.filters.title', 'Filters')}
            </span>
          </div>
          {/* Below sm the selects collapse so the list stays reachable. */}
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 gap-1 sm:hidden"
            aria-expanded={mobileFiltersOpen}
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
          >
            {mobileFiltersOpen
              ? t('pages.usersManager.filters.hide', 'Hide')
              : t('pages.usersManager.filters.show', 'Show')}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-6">
          <div className="relative xl:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t(
                'pages.usersManager.filters.searchPlaceholder',
                'Search by name, email or tax code',
              )}
              aria-label={t(
                'pages.usersManager.filters.searchPlaceholder',
                'Search by name, email or tax code',
              )}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label={t('common.clear', 'Clear')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div
            className={`${
              mobileFiltersOpen ? 'grid' : 'hidden'
            } grid-cols-1 gap-3 sm:grid sm:grid-cols-2 xl:col-span-4 xl:grid-cols-4`}
          >
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger aria-label={t('common.status', 'Status')}>
                <SelectValue placeholder={t('common.status', 'Status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('common.active', 'Active')}</SelectItem>
                <SelectItem value="inactive">{t('common.inactive', 'Inactive')}</SelectItem>
                <SelectItem value="all">
                  {t('pages.usersManager.filters.allStatuses', 'All statuses')}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger aria-label={t('pages.usersManager.columns.role', 'Role')}>
                <SelectValue placeholder={t('pages.usersManager.columns.role', 'Role')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('pages.usersManager.filters.allRoles', 'All roles')}
                </SelectItem>
                {Object.values(UserRole)
                  .filter((role) => isSuperAdmin || role !== UserRole.SUPER_ADMIN)
                  .map((role) => (
                    <SelectItem key={role} value={role}>
                      {getRoleLabel(role, t)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select value={relationshipFilter} onValueChange={setRelationshipFilter}>
              <SelectTrigger
                aria-label={t('pages.usersManager.columns.relationshipType', 'Relationship type')}
              >
                <SelectValue
                  placeholder={t(
                    'pages.usersManager.columns.relationshipType',
                    'Relationship type',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('pages.usersManager.filters.allRelationshipTypes', 'All relationship types')}
                </SelectItem>
                {relationshipTypeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {getRelationshipTypeLabel(option, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={linkFilter} onValueChange={setLinkFilter}>
              <SelectTrigger aria-label={t('pages.usersManager.supplier', 'Client/Supplier')}>
                <SelectValue placeholder={t('pages.usersManager.supplier', 'Client/Supplier')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('pages.usersManager.filters.allLinks', 'All client/suppliers')}
                </SelectItem>
                <SelectItem value="internal">
                  {t('pages.usersManager.filters.internalOnly', 'Internal only')}
                </SelectItem>

                {linkedTypes.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>
                      {t('pages.usersManager.filters.byType', 'By type')}
                    </SelectLabel>
                    {linkedTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {getClientTypeLabel(type, t)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}

                {linkedClientSuppliers.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>
                      {t('pages.usersManager.filters.byCompany', 'By company')}
                    </SelectLabel>
                    {linkedClientSuppliers.map((cs) => (
                      <SelectItem key={cs.id} value={`id:${cs.id}`}>
                        {cs.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {t('pages.usersManager.filters.results', '{{count}} users', {
              count: sortedUsers.length,
            })}
          </p>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="w-full sm:w-auto"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('pages.usersManager.filters.reset', 'Reset filters')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <ListFilter className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="font-medium">
          {hasActiveFilters
            ? t('pages.usersManager.filters.empty.title', 'No users match these filters')
            : t('pages.usersManager.noUsers', 'No users found')}
        </p>
        <p className="text-sm text-muted-foreground">
          {hasActiveFilters
            ? t(
                'pages.usersManager.filters.empty.description',
                'Change or reset the filters to see more users.',
              )
            : t(
                'pages.usersManager.emptyDescription',
                'Add the first user to start managing the team.',
              )}
        </p>
      </div>
      {hasActiveFilters ? (
        <Button variant="outline" size="sm" onClick={resetFilters}>
          <RotateCcw className="mr-2 h-4 w-4" />
          {t('pages.usersManager.filters.reset', 'Reset filters')}
        </Button>
      ) : (
        <Button size="sm" onClick={() => handleOpen()} className="bg-pink-500 hover:bg-pink-600">
          <Plus className="mr-2 h-4 w-4" />
          {t('pages.usersManager.actions.create')}
        </Button>
      )}
    </div>
  );

  /** Row actions, shared by the table and the mobile cards so they never drift. */
  const renderUserActions = (user: User, layout: 'table' | 'card') => {
    const isIconOnly = layout === 'table';
    const editLabel = t('pages.usersManager.actions.edit', 'Edit');
    const reactivateLabel = t('pages.usersManager.actions.reactivate', 'Reactivate user');
    const deleteLabel = t('common.delete', 'Delete');

    return (
      <div className={`flex items-center gap-2 ${isIconOnly ? 'justify-end' : 'justify-end pt-1'}`}>
        {user.isActive ? (
          <Button
            variant="outline"
            size={isIconOnly ? 'icon' : 'sm'}
            onClick={() => handleOpen(user)}
            className={isIconOnly ? 'h-8 w-8' : 'gap-1'}
            title={isIconOnly ? editLabel : undefined}
            aria-label={isIconOnly ? editLabel : undefined}
          >
            <Pencil className="h-4 w-4" />
            {!isIconOnly && editLabel}
          </Button>
        ) : (
          <Button
            variant="outline"
            size={isIconOnly ? 'icon' : 'sm'}
            onClick={() => openReactivateDialogForUser(user)}
            className={isIconOnly ? 'h-8 w-8' : 'gap-1'}
            title={isIconOnly ? reactivateLabel : undefined}
            aria-label={isIconOnly ? reactivateLabel : undefined}
          >
            <RotateCcw className="h-4 w-4" />
            {!isIconOnly && reactivateLabel}
          </Button>
        )}
        <Button
          variant={isIconOnly ? 'ghost' : 'destructive'}
          size={isIconOnly ? 'icon' : 'sm'}
          onClick={() => handleDeleteClick(user)}
          className={
            isIconOnly
              ? 'h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
              : 'gap-1'
          }
          title={isIconOnly ? deleteLabel : undefined}
          aria-label={isIconOnly ? deleteLabel : undefined}
        >
          <Trash2 className="h-4 w-4" />
          {!isIconOnly && deleteLabel}
        </Button>
      </div>
    );
  };

  const renderUsersTable = () => (
    <Card className="hidden md:block">
      <CardContent className="p-0">
        <Table>
          <TableHeader className="[&_tr]:border-b">
            <TableRow className="hover:bg-transparent">
              {renderSortableHead('name', t('pages.usersManager.columns.name'))}
              {renderSortableHead('role', t('pages.usersManager.columns.role'))}
              {renderSortableHead(
                'relationshipType',
                t('pages.usersManager.columns.relationshipType', 'Relationship type'),
                'hidden lg:table-cell',
              )}
              {renderSortableHead('supplier', t('pages.usersManager.supplier', 'Client/Supplier'))}
              {renderSortableHead(
                'approver',
                t('pages.usersManager.columns.approver', 'Approver'),
                'hidden lg:table-cell',
              )}
              <TableHead className="hidden xl:table-cell">
                {t('pages.usersManager.columns.codiceFiscale', 'Tax code')}
              </TableHead>
              {renderSortableHead('status', t('common.status', 'Status'))}
              <TableHead className="w-[100px] text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow key={user.id} className={user.isActive ? '' : 'bg-muted/20'}>
                <TableCell className="max-w-[280px]">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        user.isActive
                          ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                      aria-hidden="true"
                    >
                      {getInitials(user.name)}
                    </span>
                    <div className="min-w-0">
                      <p className={`truncate font-medium ${user.isActive ? '' : 'text-muted-foreground'}`}>
                        {user.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {getRoleLabel(user.role, t)}
                  </Badge>
                </TableCell>
                <TableCell className="hidden text-muted-foreground lg:table-cell">
                  {getRelationshipTypeLabel(user.relationshipType, t)}
                </TableCell>
                <TableCell className="max-w-[200px]">{renderClientSupplierCell(user)}</TableCell>
                <TableCell className="hidden max-w-[200px] lg:table-cell">{renderApproverCell(user)}</TableCell>
                <TableCell className="hidden font-mono text-xs text-muted-foreground xl:table-cell">
                  {user.codiceFiscale || '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={user.isActive ? 'approved' : 'cancelled'}>
                    {user.isActive ? t('common.active') : t('common.inactive')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{renderUserActions(user, 'table')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  /** Below md a table stops being readable, so the card layout stays. */
  const renderUsersCards = () => (
    <div className="grid grid-cols-1 gap-3 md:hidden">
      {sortedUsers.map((user) => (
        <Card key={user.id} className={user.isActive ? '' : 'bg-muted/20'}>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      user.isActive
                        ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  >
                    {getInitials(user.name)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{user.name}</p>
                    <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <Badge variant={user.isActive ? 'approved' : 'cancelled'} className="shrink-0">
                  {user.isActive ? t('common.active') : t('common.inactive')}
                </Badge>
              </div>

              <div className="space-y-1 text-xs text-muted-foreground">
                <p>
                  {t('pages.usersManager.columns.role')}: {getRoleLabel(user.role, t)}
                </p>
                <p>
                  {t('pages.usersManager.columns.relationshipType', 'Relationship type')}:{' '}
                  {getRelationshipTypeLabel(user.relationshipType, t)}
                </p>
                <p>
                  {t('pages.usersManager.supplier', 'Client/Supplier')}:{' '}
                  {user.supplier?.name || t('pages.usersManager.internal', 'Internal')}
                </p>
                <p>
                  {t('pages.usersManager.columns.approver', 'Approver')}: {renderApproverCell(user)}
                </p>
                <p>
                  {t('pages.usersManager.columns.codiceFiscale', 'Tax code')}:{' '}
                  {user.codiceFiscale || '—'}
                </p>
              </div>

              {renderUserActions(user, 'card')}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderUsers = () => {
    if (sortedUsers.length === 0) {
      return (
        <Card>
          <CardContent className="p-0">{renderEmptyState()}</CardContent>
        </Card>
      );
    }

    return (
      <>
        {renderUsersTable()}
        {renderUsersCards()}
      </>
    );
  };
  return (
    <PageTransition>
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
            <UserCog className="h-5 w-5 text-pink-600 dark:text-pink-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('pages.usersManager.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('pages.usersManager.subtitle', 'Manage system users')}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto sm:flex-nowrap">
          <Button variant="outline" onClick={handleOpenImport} className="flex-1 gap-2 sm:flex-none">
            <Upload className="h-4 w-4" />
            {t('pages.usersManager.actions.import')}
          </Button>
          <Button onClick={() => handleOpen()} className="flex-1 bg-pink-500 hover:bg-pink-600 gap-2 sm:flex-none">
            <Plus className="h-4 w-4" />
            {t('pages.usersManager.actions.create')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-pink-500" />
        </div>
      ) : (
        <>
          {renderFilters()}
          {renderUsers()}
        </>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              {t('pages.usersManager.pendingInvites.title', 'Pending Invitations')} ({pendingInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingInvites ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-pink-500" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pages.usersManager.columns.name')}</TableHead>
                    <TableHead>{t('pages.usersManager.columns.email')}</TableHead>
                    <TableHead>{t('pages.usersManager.columns.role')}</TableHead>
                    <TableHead>{t('pages.usersManager.columns.relationshipType', 'Relationship type')}</TableHead>
                    <TableHead>{t('pages.usersManager.pendingInvites.expires', 'Expires')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map((invite) => (
                    <TableRow key={invite.id} className={invite.isExpired ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">{invite.name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{invite.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(invite.role as UserRole)}>{getRoleLabel(invite.role, t)}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {getRelationshipTypeLabel(invite.relationshipType, t)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invite.isExpired
                          ? t('pages.usersManager.pendingInvites.expired', 'Expired')
                          : format(new Date(invite.expiresAt), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRevokeInvite(invite.id)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title={t('pages.usersManager.pendingInvites.revoke', 'Revoke')}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <BottomSheet
        open={importOpen}
        onClose={handleCloseImport}
        size="wide"
        title={t('pages.usersManager.actions.import')}
        description={t('pages.usersManager.importDescription', 'Import users from CSV file.')}
      >
        <div className="relative space-y-4">
          {isImportBusy && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]">
              <div className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {previewLoading
                  ? t('pages.usersManager.previewing', 'Validating file...')
                  : t('pages.usersManager.importing', 'Importing...')}
              </div>
            </div>
          )}

          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            ref={importFileInputRef}
            onChange={handleImportFileUpload}
          />

          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDownloadImportTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                {t('pages.usersManager.downloadTemplate', 'Download template')}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {t('pages.usersManager.templateHint', 'Usa il template di esempio: contiene già intestazioni e righe dimostrative compatibili con l\'importazione.')}
            </p>
          </div>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleImportDrop}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${isDraggingFile ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/10' : 'border-muted-foreground/25'}`}
          >
            <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{t('pages.usersManager.dropzoneTitle', 'Drop your CSV file here')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedImportFile
                ? t('pages.usersManager.selectedFile', 'Selected file: {{name}}', { name: selectedImportFile.name })
                : t('pages.usersManager.dropzoneDescription', 'Upload the CSV file to preview before import.')}
            </p>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={() => importFileInputRef.current?.click()} className="gap-2" disabled={isImportBusy}>
                <Upload className="h-4 w-4" />
                {t('pages.usersManager.selectFile', 'Choose CSV file')}
              </Button>
            </div>
          </div>

          {importDialogError && <Alert variant="error">{importDialogError}</Alert>}

          {previewLoading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('pages.usersManager.previewing', 'Validating file...')}
            </div>
          )}

          {importPreview && !previewLoading && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.usersManager.importResults.total', 'Total')}</p>
                    <p className="text-2xl font-semibold">{importPreview.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.usersManager.validRows', 'Valid')}</p>
                    <p className="text-2xl font-semibold text-emerald-600">{importPreview.valid}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.usersManager.invalidRows', 'Invalid')}</p>
                    <p className="text-2xl font-semibold text-amber-600">{importPreview.errors.length}</p>
                  </CardContent>
                </Card>
              </div>

              {importPreview.unresolvedSuppliers.length > 0 && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{t('pages.usersManager.supplierMappingTitle', 'Resolve unknown suppliers')}</p>
                    <Button variant="outline" size="sm" onClick={handleRefreshPreviewWithMappings}>
                      {t('pages.usersManager.validateMappings', 'Validate mapping')}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('pages.usersManager.supplierMappingHint', 'Select an existing supplier for each unknown value found in the CSV.')}
                  </p>
                  <div className="space-y-2">
                    {importPreview.unresolvedSuppliers.map((name) => (
                      <div key={name} className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2 items-center">
                        <p className="text-sm font-medium truncate">{name}</p>
                        <Select
                          value={importSupplierMappings[name] ? String(importSupplierMappings[name]) : ''}
                          onValueChange={(value) => setImportSupplierMappings((prev) => ({ ...prev, [name]: Number(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('pages.usersManager.selectSupplier', 'Select supplier')} />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map((supplier) => (
                              <SelectItem key={supplier.id} value={String(supplier.id)}>
                                {supplier.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importPreview.unresolvedTenants.length > 0 && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{t('pages.usersManager.tenantMappingTitle', 'Resolve unknown companies')}</p>
                    <Button variant="outline" size="sm" onClick={handleRefreshPreviewWithMappings}>
                      {t('pages.usersManager.validateMappings', 'Validate mapping')}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('pages.usersManager.tenantMappingHint', 'Select an existing company for each unknown value found in the CSV.')}
                  </p>
                  <div className="space-y-2">
                    {importPreview.unresolvedTenants.map((name) => (
                      <div key={name} className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2 items-center">
                        <p className="text-sm font-medium truncate">{name}</p>
                        <Select
                          value={importTenantMappings[name] ? String(importTenantMappings[name]) : ''}
                          onValueChange={(value) => setImportTenantMappings((prev) => ({ ...prev, [name]: Number(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('pages.usersManager.selectTenant', 'Select tenant...')} />
                          </SelectTrigger>
                          <SelectContent>
                            {tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={String(tenant.id)}>
                                {tenant.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>{t('pages.usersManager.columns.name')}</TableHead>
                      <TableHead>{t('pages.usersManager.columns.email')}</TableHead>
                      <TableHead>{t('pages.usersManager.columns.role')}</TableHead>
                      <TableHead>{t('pages.usersManager.columns.tenant')}</TableHead>
                      <TableHead>{t('pages.usersManager.supplier', 'Supplier')}</TableHead>
                      <TableHead>{t('common.status', 'Status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.rows.map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>
                          <span className={rowHasFieldError(row, 'name') ? 'text-amber-600 font-medium' : ''}>
                            {row.name || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={rowHasFieldError(row, 'email') || rowHasFieldError(row, 'duplicateEmail') ? 'text-amber-600 font-medium' : ''}>
                            {row.email || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={rowHasFieldError(row, 'role') ? 'text-amber-600 font-medium' : ''}>
                            {row.role ? getRoleLabel(row.role, t) : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={rowNeedsTenantMapping(row) ? 'text-amber-600 font-medium' : ''}>
                            {row.tenantName || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={rowNeedsSupplierMapping(row) ? 'text-amber-600 font-medium' : ''}>
                            {row.supplierName || t('pages.usersManager.internal', 'Internal')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {row.valid ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                              <CheckCircle2 className="h-4 w-4" />
                              {t('pages.usersManager.rowValid', 'Valid')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setInvalidPreviewRow(row)}
                              className="inline-flex items-center gap-1 text-amber-600 text-sm underline-offset-2 hover:underline"
                            >
                              <AlertTriangle className="h-4 w-4" />
                              {t('pages.usersManager.rowInvalid', 'Invalid')}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleCloseImport} disabled={importing} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={
                !selectedImportFile ||
                !importPreview ||
                importPreview.valid === 0 ||
                importing ||
                importPreview.unresolvedTenants.some((name) => !importTenantMappings[name]) ||
                importPreview.unresolvedSuppliers.some((name) => !importSupplierMappings[name])
              }
              className="flex-1 bg-pink-500 hover:bg-pink-600"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('pages.usersManager.importing', 'Importing...')}
                </>
              ) : (
                t('pages.usersManager.confirmImport', 'Import valid rows')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        open={Boolean(invalidPreviewRow)}
        onClose={() => setInvalidPreviewRow(null)}
        title={t('pages.usersManager.invalidRowDialogTitle', 'Row error details')}
        description={
          invalidPreviewRow
            ? t('pages.usersManager.invalidRowDialogDescription', 'Row {{row}} is invalid', {
                row: invalidPreviewRow.rowNumber,
              })
            : undefined
        }
      >
        <div className="space-y-4">
          {invalidPreviewRow && <Alert variant="warning">{getInvalidRowMessage(invalidPreviewRow)}</Alert>}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setInvalidPreviewRow(null)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(isOpen) => {
          setDeleteDialogOpen(isOpen);
          if (!isOpen) setUserToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {userToDelete?.isActive
                ? t('pages.usersManager.deactivateDialogTitle', 'Deactivate user')
                : t('pages.usersManager.permanentDeleteDialogTitle', 'Permanently delete user')}
            </DialogTitle>
            <DialogDescription>
              {userToDelete?.isActive
                ? t(
                    'pages.usersManager.deactivateDialogDescription',
                    'The user {{name}} ({{email}}) will be deactivated now. You can permanently delete only after the user is inactive.',
                    { name: userToDelete?.name ?? '-', email: userToDelete?.email ?? '-' },
                  )
                : t(
                    'pages.usersManager.permanentDeleteDialogDescription',
                    'The user {{name}} ({{email}}) is already inactive. This action will permanently delete the account and cannot be undone.',
                    { name: userToDelete?.name ?? '-', email: userToDelete?.email ?? '-' },
                  )}
            </DialogDescription>
          </DialogHeader>

          {!userToDelete?.isActive && (
            <div className="space-y-2">
              {deleteStatusLoading && (
                <Alert>
                  {t('pages.usersManager.deleteStatusChecking', 'Checking permanent deletion constraints...')}
                </Alert>
              )}

              {!deleteStatusLoading && deleteStatus && !deleteStatus.canPermanentlyDelete && (
                <Alert variant="warning">
                  {deleteStatus.reason === 'HAS_REFERENCES'
                    ? t(
                        'pages.usersManager.deleteStatusBlockedReferences',
                        'Permanent deletion is blocked because this user has linked historical data (timesheets, assignments, leave requests, or audit logs).',
                      )
                    : t(
                        'pages.usersManager.deleteStatusBlockedActive',
                        'Deactivate the user first to enable permanent deletion.',
                      )}
                </Alert>
              )}

              {deleteDialogError && <Alert variant="error">{deleteDialogError}</Alert>}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setUserToDelete(null);
                setDeleteDialogError(null);
                setDeleteStatus(null);
              }}
              disabled={deletingUser}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={
                deletingUser
                || deleteStatusLoading
                || (!userToDelete?.isActive && !!deleteStatus && !deleteStatus.canPermanentlyDelete)
              }
            >
              {deletingUser ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.processing', 'Processing...')}
                </>
              ) : deleteStatusLoading ? (
                t('pages.usersManager.deleteStatusCheckingShort', 'Checking...')
              ) : userToDelete?.isActive ? (
                t('pages.usersManager.deactivateAction', 'Deactivate')
              ) : deleteStatus && !deleteStatus.canPermanentlyDelete ? (
                t('pages.usersManager.permanentDeleteUnavailableAction', 'Permanent delete unavailable')
              ) : (
                t('pages.usersManager.permanentDeleteAction', 'Delete permanently')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reactivateDialogOpen}
        onOpenChange={(isOpen) => {
          setReactivateDialogOpen(isOpen);
          if (!isOpen) setReactivateTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('pages.usersManager.reactivateDialogTitle', 'Reactivate user')}
            </DialogTitle>
            <DialogDescription>
              {reactivateTarget?.mode === 'email'
                ? t(
                    'pages.usersManager.reactivateDialogDescriptionEmail',
                    'This email belongs to an inactive user. Reactivate {{email}} now?',
                    { email: reactivateTarget?.email ?? '-' },
                  )
                : t(
                    'pages.usersManager.reactivateDialogDescriptionUser',
                    'Reactivate user {{name}} ({{email}})?',
                    {
                      name: reactivateTarget?.user?.name ?? '-',
                      email: reactivateTarget?.email ?? '-',
                    },
                  )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReactivateDialogOpen(false);
                setReactivateTarget(null);
              }}
              disabled={reactivatingUser}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirmReactivate} disabled={reactivatingUser}>
              {reactivatingUser ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.processing', 'Processing...')}
                </>
              ) : (
                t('pages.usersManager.reactivateAction', 'Reactivate')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? t('pages.usersManager.actions.edit') : t('pages.usersManager.actions.create')}
            </DialogTitle>
            {editingUser && (
              <DialogDescription>
                {t('pages.usersManager.editDescription', 'Update user information')}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            {formError && !inviteSent && <Alert variant="error">{formError}</Alert>}
            {inviteSent ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="font-medium">{t('pages.usersManager.inviteSent.title', 'Invite sent!')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('pages.usersManager.inviteSent.description', 'An email has been sent to {{email}} with a link to set their password.', { email: formData.email })}
                </p>
              </div>
            ) : (
              <>
            <div className="space-y-2">
              <Label htmlFor="name">{t('pages.usersManager.columns.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('pages.usersManager.namePlaceholder', 'John Doe')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('pages.usersManager.columns.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="codiceFiscale">{t('pages.usersManager.columns.codiceFiscale', 'Tax code')}</Label>
              <Input
                id="codiceFiscale"
                value={formData.codiceFiscale}
                onChange={(e) => setFormData({ ...formData, codiceFiscale: e.target.value.toUpperCase() })}
                placeholder={t('pages.usersManager.codiceFiscalePlaceholder', 'RSSMRA85M01H501Z')}
                maxLength={16}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('pages.usersManager.columns.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => {
                  const role = value as UserRole;
                  setFormData({
                    ...formData,
                    role,
                    approverId: isSelfApprovingRole(role) ? null : formData.approverId,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages.usersManager.selectRole', 'Select role')} />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {getRoleLabel(role, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('pages.usersManager.columns.approver', 'Approver')}</Label>
              {needsApprover ? (
                <>
                  <Select
                    value={formData.approverId ? String(formData.approverId) : ''}
                    onValueChange={(value) => setFormData({ ...formData, approverId: Number(value) })}
                    disabled={loadingApprovers || approverOptions.length === 0}
                  >
                    <SelectTrigger aria-label={t('pages.usersManager.columns.approver', 'Approver')}>
                      <SelectValue
                        placeholder={
                          loadingApprovers
                            ? t('pages.usersManager.loadingApprovers', 'Loading approvers...')
                            : t('pages.usersManager.selectApprover', 'Select approver...')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {approverOptions.map((candidate) => (
                        <SelectItem key={candidate.id} value={String(candidate.id)}>
                          {candidate.name} · {getRoleLabel(candidate.role, t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!loadingApprovers && approverOptions.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('pages.usersManager.noApproverCandidates', 'No approver available: create a Manager or Admin user first.')}
                    </p>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed px-3 py-2 text-sm">
                  <span className="font-medium">{t('pages.usersManager.selfApprover', 'Self')}</span>
                  <p className="text-xs text-muted-foreground">
                    {t('pages.usersManager.selfApproverHint', 'Admins and Super Admins have nobody above them: they approve themselves.')}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('pages.usersManager.columns.relationshipType', 'Relationship type')}</Label>
              <Select
                value={formData.relationshipType}
                onValueChange={(value) => setFormData({ ...formData, relationshipType: value as UserRelationshipType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages.usersManager.selectRelationshipType', 'Select relationship type')} />
                </SelectTrigger>
                <SelectContent>
                  {relationshipTypeOptions.map((relationshipType) => (
                    <SelectItem key={relationshipType} value={relationshipType}>
                      {getRelationshipTypeLabel(relationshipType, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showSupplierField && (
              <div className="space-y-2">
                <Label>{t('pages.usersManager.supplier', 'Client/Supplier')}</Label>
                <Select
                  value={formData.supplierId ? String(formData.supplierId) : ''}
                  onValueChange={(value) =>
                    setFormData({ ...formData, supplierId: Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.usersManager.selectSupplier', 'Select client/supplier')} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              {inviteSent ? t('common.close', 'Close') : t('common.cancel')}
            </Button>
            {!inviteSent && (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-pink-500 hover:bg-pink-600"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : editingUser ? (
                t('common.save')
              ) : (
                t('pages.usersManager.actions.sendInvite', 'Send Invite')
              )}
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageTransition>
  );
};

export default AdminUsersManager;
